import { type ReactNode, forwardRef } from 'react';
import { Platform, ScrollView, type ScrollViewProps, StyleSheet, View } from 'react-native';

import { useMenuClickEvents } from '@fhs/menu/src/analytics';

import { tokens } from '../../tokens';
import { Anchor } from '../anchor';

import { useAnchors } from './use-anchors';
import { useScrollIntoFirstPositionInView } from './use-scroll-into-first-position-in-view';

type HorizontalScrollerProps = Omit<
  ScrollViewProps,
  'horizontal' | 'showsHorizontalScrollIndicator' | 'alwaysBounceHorizontal' | 'bounces'
>;

const HorizontalScroller = forwardRef<ScrollView, HorizontalScrollerProps>(
  (props, forwardedRef) => {
    const {
      accessibilityLabel = 'Menu categories',
      children,
      contentContainerStyle,
      style,
      ...scrollProps
    } = props;

    return (
      <ScrollView
        testID="anchor-list-horizontal"
        ref={forwardedRef}
        {...scrollProps}
        accessible={false}
        importantForAccessibility="no"
        horizontal
        alwaysBounceHorizontal={false}
        bounces={false}
        showsHorizontalScrollIndicator={false}
        style={[horizontalScrollerStyles.scrollView, style]}
      >
        <View
          accessibilityRole="tablist"
          accessibilityLabel={accessibilityLabel}
          {...(Platform.OS === 'web'
            ? ({ 'aria-label': accessibilityLabel } as const)
            : {})}
          style={[horizontalScrollerStyles.scrollViewContent, contentContainerStyle]}
        >
          {children}
        </View>
      </ScrollView>
    );
  }
);

const SpaceFiller = () => (
  <View
    accessible={false}
    accessibilityElementsHidden
    importantForAccessibility="no"
    {...(Platform.OS === 'web' ? ({ 'aria-hidden': true } as const) : {})}
    style={horizontalScrollerStyles.spaceFiller}
  />
);

const horizontalScrollerStyles = StyleSheet.create({
  scrollView: {
    flexGrow: 0,
    backgroundColor: tokens.colors.$white,
    minWidth: '100%',
    //@ts-ignore Web only style
    ...Platform.select({
      web: {
        overscrollBehavior: 'none',
      },
      default: {},
    }),
  },
  scrollViewContent: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    minWidth: '100%',
  },
  spaceFiller: {
    flexGrow: 9999,
    flexShrink: 0,
    borderBottomWidth: 3,
    borderColor: tokens.colors.$blackOpacity10,
  },
});

export type AnchorListHorizontalProps = HorizontalScrollerProps & {
  contentStart?: ReactNode;
  contentEnd?: ReactNode;
};
export function AnchorListHorizontal({
  contentStart = <SpaceFiller />,
  contentEnd = <SpaceFiller />,
  ...horizontalScrollerProps
}: AnchorListHorizontalProps) {
  const { handleScrollerLayout, handleScrollerScroll, scrollerRef, scrollIntoFirstPositionInView } =
    useScrollIntoFirstPositionInView('horizontal');

  const anchors = useAnchors();
  const { logMenuTabClick } = useMenuClickEvents();

  return (
    <HorizontalScroller
      ref={scrollerRef}
      onLayout={handleScrollerLayout}
      onScroll={handleScrollerScroll}
      scrollEventThrottle={200}
      {...horizontalScrollerProps}
    >
      {contentStart ? contentStart : null}
      {anchors.map((a, index) => (
        <Anchor
          key={`${a.id}-${index}`}
          isActive={a.isActive}
          accessibilityRole="tab"
          accessibilityState={{ selected: a.isActive }}
          onPress={() => {
            a.onPress();
            logMenuTabClick(a.title);
          }}
          title={a.title}
          scrollIntoFirstPositionInView={scrollIntoFirstPositionInView}
        />
      ))}
      {contentEnd ? contentEnd : null}
    </HorizontalScroller>
  );
}
